import { Disclosure } from "@headlessui/react"
import { CaretUp, Files, Pencil, User } from "@phosphor-icons/react"
import { CodenameTag, Tag, Toast } from "@vesatogo/grass-core"
import classNames from "classnames"
import dayjs from "dayjs"
import toast from "react-hot-toast"
import { useNavigate } from "react-router-dom"
import { UserKindsReadable } from "~/constants/Kinds"

export type PattiSummaryProps = {
  data: any
  type: "demand" | "offer"
}

export const PattiSummary = ({ data, type }: PattiSummaryProps) => {
  const navigate = useNavigate()
  const patti = data
  const isDemand = type === "demand"
  const startDate = dayjs(patti?.start_date_time)
  const isPending = true
  // startDate.isAfter(dayjs())
  return (
    <Disclosure
      as="div"
      className="border-1 border-gray-300 rounded-xl bg-gray-100 overflow-hidden"
      defaultOpen
    >
      <Disclosure.Button className="flex justify-between items-center px-4 py-3 border-b border-gray-100 bg-gray-50 w-full">
        <div className="flex items-center gap-2">
          <Files size={20} />
          <span className="font-500 text-gray-800">Patti Summary</span>
        </div>
        <div className="flex items-center gap-1 text-xs text-gray-500">
          <span>Patti ID: #{patti?.id}</span>|
          <span>{dayjs(patti?.created_at).format("DD MMM 'YY | h:mm A")}</span>
          |
          <Pencil
            size={18}
            className={classNames("text-gray-500", {
              "text-gray-400": !isPending,
            })}
            onClick={e => {
              e.stopPropagation()
              if (isPending) {
                navigate(`/d/patti/${patti?.id}?type=${type}`)
              } else {
                toast.custom(
                  <Toast
                    title={
                      (isDemand ? "Demand" : "Offer") +
                      " is live or has ended. You do not have permission to edit."
                    }
                    intent="danger"
                  />
                )
              }
            }}
          />
          |
          <CaretUp size={18} className="text-gray-800" />
        </div>
      </Disclosure.Button>

      <Disclosure.Panel>
        <div className="p-4 grid grid-cols-3 gap-y-4 gap-x-8 text-sm">
          <div className="flex flex-col gap-1">
            <span className=" text-gray-800">
              {isDemand ? "Buyer Name" : "Seller Name"}
            </span>
            <div className="flex items-center gap-2">
              <span className="text-gray-600 ">
                {isDemand
                  ? patti?.buyer?.user?.[0]?.name
                  : patti?.seller?.user?.[0]?.name}
              </span>
              <CodenameTag
                leftIcon={<User weight="fill" />}
                referenceId={
                  isDemand
                    ? patti?.buyer?.user?.[0]?.external_reference_id
                    : patti?.seller?.user?.[0]?.external_reference_id
                }
              />
            </div>
          </div>

          <div className="flex flex-col gap-1">
            <span className=" text-gray-800">Commodity</span>
            <span className="text-gray-600 ">
              {patti?.commodity?.commodity?.[0]?.name}
            </span>
          </div>

          <div className="flex flex-col gap-1">
            <span className=" text-gray-800">Variety</span>
            <span className="text-gray-600 ">
              {patti?.commodity_variety?.commodity_variety?.[0]?.name}
            </span>
          </div>

          <div className="flex flex-col gap-1">
            <span className=" text-gray-800">Grade</span>
            {patti?.variety_grade?.variety_grade?.[0]?.name ? (
              <Tag
                intent="info"
                variant="minimal"
                className="w-fit !rounded-xl"
              >
                {patti?.variety_grade?.variety_grade?.[0]?.name}
              </Tag>
            ) : (
              <span className="text-gray-600">N/A</span>
            )}
          </div>

          <div className="flex flex-col gap-1">
            <span className=" text-gray-800">Rate</span>
            <div className="flex items-center gap-1">
              <span className=" text-brown-400">₹{patti?.rate}/kg</span>
              {patti?.max_rate && (
                <span className="  text-brown-400">
                  - ₹{patti?.max_rate}/kg
                </span>
              )}
              <span className=" text-gray-800">|</span>
              <span className=" text-brown-400">
                ₹{patti?.total_amount?.toLocaleString()}
              </span>
            </div>
          </div>

          <div className="flex flex-col gap-1">
            <span className=" text-gray-800">Quantity</span>
            <span className=" text-brown-400">
              {patti?.quantity?.toLocaleString()} kg
            </span>
          </div>

          <div className="flex flex-col gap-1">
            <span className=" text-gray-800">
              {isDemand ? "Seller" : "Buyer"} Type
            </span>
            <span className="text-gray-600 ">
              {isDemand
                ? UserKindsReadable[patti?.seller_source_kind]
                : UserKindsReadable[patti?.buyer_source_kind]}
            </span>
          </div>

          <div className="flex flex-col gap-1">
            <span className=" text-gray-800">Users</span>
            <span className="text-gray-600 ">
              {patti?.total_enquiries?.aggregate?.count}
            </span>
          </div>

          <div className="flex flex-col gap-1">
            <span className=" text-gray-800">Placed By</span>
            <span className="text-gray-600 ">
              {patti?.created_by?.full_name}
            </span>
          </div>
        </div>

        <div className="px-4 pb-4 text-sm">
          {/* <span className=" text-gray-800">
            {isDemand ? "Seller" : "Buyer"} Name
          </span>
          <div className="flex flex-wrap gap-2 mt-1">
            {patti?.enquiries?.map((s: any, idx: number) => (
              <Tag
                key={idx}
                variant="minimal"
                intent="info"
                className=" px-2 !rounded-xl"
              >
                {isDemand
                  ? s?.seller?.user?.[0]?.name ||
                    s?.seller_external?.first_name +
                      " " +
                      s?.seller_external?.last_name
                  : s?.buyer?.user?.[0]?.name ||
                    s?.buyer_external?.first_name +
                      " " +
                      s?.buyer_external?.last_name}
              </Tag>
            ))}
          </div> */}

          <div className="!p-2 rounded-lg grid grid-cols-2 gap-4 text-sm bg-white mt-4">
            <div>
              <span className="text-gray-800 block">
                {isDemand ? "Demand " : "Offer "} Starts
              </span>
              <span className=" text-gray-600">
                {dayjs(patti?.start_date_time).format("DD MMM 'YY | h:mm A")}
              </span>
            </div>
            <div>
              <span className="text-gray-800 block">
                {isDemand ? "Demand " : "Offer "} Ends
              </span>
              <span className=" text-gray-600">
                {dayjs(patti?.end_date_time).format("DD MMM 'YY | h:mm A")}
              </span>
            </div>
          </div>
        </div>
      </Disclosure.Panel>
    </Disclosure>
    // </div>
  )
}
